import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';

interface OnboardingProgress {
  sellerSelectCompleted: boolean;
  planSelected: boolean;
  termsAccepted: boolean;
  storeCompleted: boolean;
  currentStep: number;
  lastUpdated: string;
  autojoinApplied?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OnboardingSequenceGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const user = JSON.parse(localStorage.getItem('userDB') || '{}');

    // Only apply to sellers in onboarding
    if (user.type !== 'seller') {
      return true;
    }

    // Check if seller has completed onboarding
    if (this.isOnboardingComplete()) {
      return true;
    }

    const targetUrl = state.url;
    const progress = this.getOnboardingProgress();

    // Define route-to-step mapping
    const routeSteps: { [key: string]: number } = {
      '/seller-select': 0,
      '/plans': 1,
      '/logged-seller/store': 3
    };

    // Get the step for the target route
    let targetStep = -1;
    for (const [routePath, step] of Object.entries(routeSteps)) {
      if (targetUrl.includes(routePath)) {
        targetStep = step;
        break;
      }
    }

    // If not an onboarding route, allow navigation
    if (targetStep === -1) {
      return true;
    }

    // Check if user is trying to access a future step
    const currentStep = this.getCurrentStep(progress);

    if (targetStep > currentStep) {
      // User is trying to skip steps - redirect to current step
      return this.redirectToCurrentStep(currentStep);
    }

    // Check if user is trying to go back
    if (targetStep < currentStep && targetStep !== currentStep) {
      // Prevent going back during onboarding
      return this.redirectToCurrentStep(currentStep);
    }

    // Allow access to current or appropriate step
    return true;
  }

  private getOnboardingProgress(): OnboardingProgress {
    const savedProgress = localStorage.getItem('onboardingProgress');
    if (savedProgress) {
      return JSON.parse(savedProgress);
    }

    // Default progress
    return {
      sellerSelectCompleted: false,
      planSelected: false,
      termsAccepted: false,
      storeCompleted: false,
      currentStep: 0,
      lastUpdated: new Date().toISOString()
    };
  }

  private getCurrentStep(progress: OnboardingProgress): number {
    // Determine current step based on what's completed
    if (!progress.sellerSelectCompleted) {
      return 0; // seller-select
    }
    if (!progress.planSelected) {
      return 1; // plans
    }
    if (!progress.termsAccepted) {
      return 2; // terms (same page as plans)
    }
    if (!progress.storeCompleted) {
      return 3; // store
    }

    return 4; // All complete
  }

  private redirectToCurrentStep(currentStep: number): UrlTree {
    switch (currentStep) {
      case 0:
        return this.router.createUrlTree(['/seller-select']);
      case 1:
      case 2:
        return this.router.createUrlTree(['/plans']);
      case 3:
        return this.router.createUrlTree(['/logged-seller/store']);
      default:
        return this.router.createUrlTree(['/logged-seller/home']);
    }
  }

  private isOnboardingComplete(): boolean {
    const progress = this.getOnboardingProgress();
    return progress.sellerSelectCompleted &&
           progress.planSelected &&
           progress.termsAccepted &&
           progress.storeCompleted;
  }

  /**
   * Helper method to mark a step as completed
   * Should be called from components when user completes a step
   */
  static markStepCompleted(stepId: 'seller-select' | 'plans' | 'terms' | 'store') {
    const savedProgress = localStorage.getItem('onboardingProgress');
    let progress: OnboardingProgress;

    if (savedProgress) {
      progress = JSON.parse(savedProgress);
    } else {
      progress = {
        sellerSelectCompleted: false,
        planSelected: false,
        termsAccepted: false,
        storeCompleted: false,
        currentStep: 0,
        lastUpdated: new Date().toISOString()
      };
    }

    // Mark the specific step as completed
    switch (stepId) {
      case 'seller-select':
        progress.sellerSelectCompleted = true;
        progress.currentStep = Math.max(progress.currentStep, 1);
        break;
      case 'plans':
        progress.planSelected = true;
        progress.currentStep = Math.max(progress.currentStep, 2);
        break;
      case 'terms':
        progress.termsAccepted = true;
        progress.currentStep = Math.max(progress.currentStep, 3);
        break;
      case 'store':
        progress.storeCompleted = true;
        progress.currentStep = Math.max(progress.currentStep, 4);
        break;
    }

    progress.lastUpdated = new Date().toISOString();
    localStorage.setItem('onboardingProgress', JSON.stringify(progress));

    // Dispatch custom event to notify components about progress update
    const event = new CustomEvent('onboardingProgressUpdated', {
      detail: { stepId, progress }
    });
    window.dispatchEvent(event);
  }

  /**
   * Persist that the backend auto-linked the seller to a plan during onboarding,
   * so downstream UI can hide the plan/contract steps.
   */
  static markAutojoinApplied() {
    const savedProgress = localStorage.getItem('onboardingProgress');
    let progress: OnboardingProgress;

    if (savedProgress) {
      progress = JSON.parse(savedProgress);
    } else {
      progress = {
        sellerSelectCompleted: false,
        planSelected: false,
        termsAccepted: false,
        storeCompleted: false,
        currentStep: 0,
        lastUpdated: new Date().toISOString()
      };
    }

    progress.autojoinApplied = true;
    progress.lastUpdated = new Date().toISOString();
    localStorage.setItem('onboardingProgress', JSON.stringify(progress));

    const event = new CustomEvent('onboardingProgressUpdated', {
      detail: { stepId: 'autojoin', progress }
    });
    window.dispatchEvent(event);
  }

  static isAutojoinApplied(): boolean {
    const savedProgress = localStorage.getItem('onboardingProgress');
    if (!savedProgress) {
      return false;
    }
    try {
      const progress: OnboardingProgress = JSON.parse(savedProgress);
      return !!progress.autojoinApplied;
    } catch {
      return false;
    }
  }

  /**
   * Helper method to check if seller is in onboarding mode
   */
  static isInOnboarding(): boolean {
    const user = JSON.parse(localStorage.getItem('userDB') || '{}');
    if (user.type !== 'seller') {
      return false;
    }

    const savedProgress = localStorage.getItem('onboardingProgress');
    if (!savedProgress) {
      return true; // New seller, consider in onboarding
    }

    const progress: OnboardingProgress = JSON.parse(savedProgress);
    return !(progress.sellerSelectCompleted &&
             progress.planSelected &&
             progress.termsAccepted &&
             progress.storeCompleted);
  }

  /**
   * Reset onboarding progress (useful for testing or if user needs to redo)
   */
  static resetOnboarding() {
    localStorage.removeItem('onboardingProgress');
  }
}
